import axios from "axios";
import {ApiResponseDto, VivoRootModel} from "@utils/Models";
import {JwtUtils} from "./JwtUtils";

/**
 * Latest-request-wins guard for everything that replaces the authenticated root model (lab switch, division switch,
 * token verification after login/impersonation). A response is only applied when no newer context request has
 * started since and the token has not changed, so an older lab/division/token response can never overwrite a
 * newer decision (e.g. a stale Customer Management capability).
 */
let latestContextRequestId = 0;

/** Marks the start of a context request; the returned function tells whether it is still the current one. */
export function beginContextRequest(): () => boolean {
    const requestId = ++latestContextRequestId;
    const token = JwtUtils.getToken();
    return () => requestId === latestContextRequestId && JwtUtils.getToken() === token;
}

/** Makes every context request that is still in flight obsolete (e.g. when the token changes). */
export function invalidateContextRequests() {
    latestContextRequestId++;
}

/**
 * POSTs a context change (SwitchLab / SwitchDivision) and returns the new root model, or null when a newer
 * context request or a token change superseded this one while it was in flight (the caller must then do nothing).
 * Request errors are rethrown.
 */
export async function requestContextChange(url: string, id: number): Promise<VivoRootModel | null> {
    const isCurrent = beginContextRequest();
    const response = await axios.post<ApiResponseDto<VivoRootModel>>(url, JSON.stringify(id));
    return isCurrent() ? response.data.Result : null;
}

/**
 * Explicit outcome of an authentication/context operation that was overtaken by a newer context request or a token
 * change. It is neither a success nor a failure: consumers must treat it as a no-op (no model/claims/error/
 * initialization writes and, above all, no logout of the newer identity).
 */
export const SUPERSEDED = Object.freeze({superseded: true as const});
export type Superseded = typeof SUPERSEDED;

export function isSuperseded(value: unknown): value is Superseded {
    return value === SUPERSEDED;
}

/**
 * Guards the WHOLE token-issuing phase (OneLogin exchange, credentials login, impersonation request): the freshness
 * generation starts before the request and is checked before the caller may adopt/save any token. A newer context
 * request, token change or invalidation while the request was pending makes the result SUPERSEDED - success and
 * failure alike, so an obsolete failure is a no-op (no error message, no logout) and an obsolete token is never saved.
 * Errors of a still-current request are rethrown unchanged. Adopting the issued token deliberately invalidates this
 * generation afterwards and hands over to the new-token VerifyUser, which owns the next generation.
 * `request` receives `isCurrent` so it can stop between its own awaits.
 */
export async function runTokenIssuingRequest<T>(request: (isCurrent: () => boolean) => Promise<T>): Promise<T | Superseded> {
    const isCurrent = beginContextRequest();
    try {
        const result = await request(isCurrent);
        return isCurrent() ? result : SUPERSEDED;
    } catch (error) {
        if (!isCurrent())
            return SUPERSEDED;
        throw error;
    }
}
